import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

import { CfsApi, DataApi, MapLayerApi, TaskApi } from '@/api';
import { LocalStorageItems } from '@/constants';
import {
  ICFSCloseReason,
  IIncidentCode,
  IPolygonLayer,
  ITag,
  ITaskStatus,
  ITaskType,
  IUnitType,
  IVehicleMake,
  PolygonLayerType,
} from '@/models';

// 5 minutes in milliseconds
const REFERENCE_DATA_TTL_MS = 5 * 60 * 1000;

let beatsFetchPromise: Promise<void> | null = null;

interface DataStoreState {
  makes: IVehicleMake[];
  unitTypes: IUnitType[];
  isUnitTypesLoading: boolean;
  incidentCodes: IIncidentCode[];
  incidentCodesAgencyId: string | null;
  incidentCodesLastFetchedAt: number | null;
  beats: IPolygonLayer[];
  beatsLastFetchedAt: number | null;
  cfsCloseReasons: ICFSCloseReason[];
  taskTypes: ITaskType[];
  taskTags: ITag[];
  taskStatuses: ITaskStatus[];
}

interface DataStore extends DataStoreState {
  updateData: (newData: Partial<DataStoreState>) => void;
  loadData: () => void;
  refreshMakes: () => void;
  fetchUnitTypes: (agencyId?: string) => Promise<void>;
  fetchAccountUnitTypes: () => Promise<void>; // New method for account-wide unit types
  fetchBeats: () => Promise<void>;
}

export const useDataStore = create<
  DataStore,
  [['zustand/devtools', never], ['zustand/persist', DataStoreState]]
>(
  devtools(
    persist(
      (set, get) => ({
        incidentCodes: [],
        incidentCodesAgencyId: null,
        incidentCodesLastFetchedAt: null,
        makes: [],
        unitTypes: [],
        isUnitTypesLoading: false,
        beats: [],
        beatsLastFetchedAt: null,
        cfsCloseReasons: [],
        taskTypes: [],
        taskTags: [],
        taskStatuses: [],
        updateData: (newData: Partial<DataStoreState>) =>
          set((state) => ({
            ...state,
            ...newData,
          })),
        refreshMakes: () => {
          DataApi.getVehicleMakes().then((res) => {
            set((state) => ({ ...state, makes: res.data }));
          });
        },
        loadData: () => {
          CfsApi.getCloseReasons().then((res) => {
            set((state) => ({ ...state, cfsCloseReasons: res.data }));
          });
          DataApi.getVehicleMakes().then((res) => {
            set((state) => ({ ...state, makes: res.data }));
          });
          get().fetchUnitTypes();

          // Get the current agency ID from localStorage
          const agencyId = localStorage.getItem(
            LocalStorageItems.CurrentAgencyId,
          );

          // Skip re-fetching incident codes if they are fresh for the current agency
          const currentState = get();
          const now = Date.now();
          const isIncidentCodesFresh =
            currentState.incidentCodes.length > 0 &&
            currentState.incidentCodesAgencyId === agencyId &&
            currentState.incidentCodesLastFetchedAt !== null &&
            now - currentState.incidentCodesLastFetchedAt <
              REFERENCE_DATA_TTL_MS;

          if (!isIncidentCodesFresh) {
            DataApi.getIncidentCodes(agencyId || undefined).then((res) => {
              set((state) => ({
                ...state,
                incidentCodes: res.data,
                incidentCodesAgencyId: agencyId,
                incidentCodesLastFetchedAt: Date.now(),
              }));
            });
          }

          TaskApi.getTaskTypes().then((res) => {
            set((state) => ({ ...state, taskTypes: res.data }));
          });
          TaskApi.getTaskStatuses().then((res) => {
            set((state) => ({ ...state, taskStatuses: res.data }));
          });
          TaskApi.getTaskTags().then((res) => {
            set((state) => ({ ...state, taskTags: res.data }));
          });
        },
        fetchUnitTypes: async (agencyId?: string) => {
          const currentAgencyId =
            agencyId || localStorage.getItem(LocalStorageItems.CurrentAgencyId);

          if (!currentAgencyId) return;

          localStorage.setItem(
            LocalStorageItems.CurrentAgencyId,
            currentAgencyId,
          );

          set((state) => ({
            ...state,
            isUnitTypesLoading: true,
          }));

          try {
            const res = await DataApi.getUnitTypes();
            set((state) => ({
              ...state,
              unitTypes: res.data,
              isUnitTypesLoading: false,
            }));
          } catch {
            set((state) => ({
              ...state,
              isUnitTypesLoading: false,
            }));
          }
        },
        fetchBeats: async () => {
          const { beatsLastFetchedAt } = get();
          const areFresh =
            beatsLastFetchedAt !== null &&
            Date.now() - beatsLastFetchedAt < REFERENCE_DATA_TTL_MS;
          if (areFresh) return;

          if (!beatsFetchPromise) {
            beatsFetchPromise = MapLayerApi.getPolygonLayers({
              type: PolygonLayerType.BEAT,
            })
              .then((res) => {
                set((state) => ({
                  ...state,
                  beats: res.data.results,
                  beatsLastFetchedAt: Date.now(),
                }));
              })
              .catch(() => {
                // Keep whatever is cached; the next call retries after the guard clears.
              })
              .finally(() => {
                beatsFetchPromise = null;
              });
          }
          return beatsFetchPromise;
        },
        fetchAccountUnitTypes: async () => {
          // eslint-disable-next-line no-useless-catch
          try {
            set((state) => ({ ...state, isUnitTypesLoading: true }));
            const res = await DataApi.getUnitTypes(true);
            set((state) => ({
              ...state,
              unitTypes: res.data,
              isUnitTypesLoading: false,
            }));
          } catch (error) {
            set((state) => ({ ...state, isUnitTypesLoading: false }));
            throw error;
          }
        },
      }),
      {
        name: 'dataStore',
        version: 1,
        storage: createJSONStorage(() => localStorage),
      },
    ),
  ),
);
